import { useState, FormEvent } from 'react';
import { useInView } from '@/hooks/useInView';

export default function ContactSection() {
  const { ref, inView } = useInView();
  const [contactDone, setContactDone] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'booking'>('contact');

  const handleContact = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('your-phone', (form.elements.namedItem('your-phone') as HTMLInputElement).value);
    try {
      await fetch('https://readdy.ai/api/form/d7efoqj5bqmofchk7fgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } finally {
      setContactLoading(false);
      setContactDone(true);
    }
  };

  const handleBooking = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBookingLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('name', (form.elements.namedItem('name') as HTMLInputElement).value);
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('your-phone', (form.elements.namedItem('your-phone') as HTMLInputElement).value);
    data.append('organization', (form.elements.namedItem('organization') as HTMLInputElement).value);
    data.append('preferred-date', (form.elements.namedItem('preferred-date') as HTMLInputElement).value);
    data.append('message', (form.elements.namedItem('message') as HTMLTextAreaElement).value);
    try {
      await fetch('https://readdy.ai/api/form/d7efoqj5bqmofchk7fh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } finally {
      setBookingLoading(false);
      setBookingDone(true);
    }
  };

  return (
    <section id="contact" ref={ref as React.RefObject<HTMLElement>} className="bg-gray-900 py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-yellow-400 mb-3">Get in Touch</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white font-syne mb-3">Let's work together</h2>
          <p className="text-gray-400 text-sm">Get a 5% discount when you order today</p>
          <a href="tel:+380635960309" className="inline-block mt-3 text-yellow-400 font-bold text-lg hover:text-yellow-300 transition-colors">+380 63 596 03 09</a>
        </div>

        <div className={`transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Tab switcher */}
          <div id="booking" className="flex bg-gray-800 rounded-full p-1 mb-8 max-w-sm mx-auto">
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${activeTab === 'contact' ? 'bg-yellow-400 text-gray-900' : 'text-gray-400 hover:text-white'}`}
            >
              Contact Us
            </button>
            <button
              onClick={() => setActiveTab('booking')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${activeTab === 'booking' ? 'bg-yellow-400 text-gray-900' : 'text-gray-400 hover:text-white'}`}
            >
              Book a Meeting
            </button>
          </div>

          <div className="bg-gray-800 rounded-3xl p-8 md:p-10 max-w-xl mx-auto">
            {/* Contact Form */}
            {activeTab === 'contact' && (
              contactDone ? (
                <div className="text-center py-8">
                  <i className="ri-checkbox-circle-fill text-5xl text-yellow-400 mb-4 block"></i>
                  <h3 className="text-xl font-bold text-white mb-2">Thank you!</h3>
                  <p className="text-gray-400 text-sm">We'll contact you shortly with your 5% discount.</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleContact} className="flex flex-col gap-4">
                  <h3 className="text-lg font-bold text-white mb-2">Get a 5% Discount</h3>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email *</label>
                    <input type="email" name="email" required placeholder="your@email.com"
                      className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Phone *</label>
                    <input type="tel" name="your-phone" required placeholder="+380 XX XXX XX XX"
                      className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                  </div>
                  <button type="submit" disabled={contactLoading}
                    className="bg-yellow-400 text-gray-900 font-bold py-3.5 rounded-full hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 mt-2">
                    {contactLoading ? 'Sending...' : 'Get My Discount'}
                  </button>
                </form>
              )
            )}

            {/* Booking Form */}
            {activeTab === 'booking' && (
              bookingDone ? (
                <div className="text-center py-8">
                  <i className="ri-calendar-check-fill text-5xl text-yellow-400 mb-4 block"></i>
                  <h3 className="text-xl font-bold text-white mb-2">Meeting Booked!</h3>
                  <p className="text-gray-400 text-sm">We'll confirm your meeting time shortly.</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleBooking} className="flex flex-col gap-4">
                  <h3 className="text-lg font-bold text-white mb-2">Book a Meeting</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Name *</label>
                      <input type="text" name="name" required placeholder="Your name"
                        className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email *</label>
                      <input type="email" name="email" required placeholder="your@email.com"
                        className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Phone *</label>
                      <input type="tel" name="your-phone" required placeholder="+380 XX XXX XX XX"
                        className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Organization</label>
                      <input type="text" name="organization" placeholder="School / NGO"
                        className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Preferred Date</label>
                    <input type="date" name="preferred-date"
                      className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-400 transition-colors" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Message</label>
                    <textarea name="message" rows={3} maxLength={500} placeholder="Tell us about your needs..."
                      className="bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors resize-none" />
                  </div>
                  <button type="submit" disabled={bookingLoading}
                    className="bg-yellow-400 text-gray-900 font-bold py-3.5 rounded-full hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 mt-2">
                    {bookingLoading ? 'Booking...' : 'Book Meeting'}
                  </button>
                </form>
              )
            )}
          </div>
        </div>

        {/* Bottom features */}
        <div className={`mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { img: 'https://storage.googleapis.com/storage.nanitrobot.com/wp-content/uploads/2022/09/bottom_1.svg', title: 'Online Payment', desc: 'All types of payments accepted' },
            { img: 'https://storage.googleapis.com/storage.nanitrobot.com/wp-content/uploads/2022/09/bottom_2.svg', title: 'Worldwide Delivery', desc: 'Courier delivery across Ukraine and worldwide' },
            { img: 'https://storage.googleapis.com/storage.nanitrobot.com/wp-content/uploads/2022/09/bottom_3.svg', title: 'Quality Products', desc: 'High-quality, health-safe materials' },
            { img: 'https://storage.googleapis.com/storage.nanitrobot.com/wp-content/uploads/2022/09/bottom_4.svg', title: 'Eco Friendly', desc: 'We reduce plastic use by 50 kg' },
          ].map((item) => (
            <div key={item.title} className="flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-xl">
                <img src={item.img} alt={item.title} className="w-7 h-7 object-contain brightness-0 invert" />
              </div>
              <h4 className="text-xs font-bold text-white uppercase tracking-wide">{item.title}</h4>
              <p className="text-xs text-gray-500 leading-snug">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
